import React, { useState, useEffect, useRef } from "react";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowsLeftRight,
  CreditCard,
  Wallet,
  Gift,
  CheckCircle,
  Minus,
  TrendUp,
  Handshake,
  Gauge,
  Stack,
  Lightning,
  LinkedinLogo,
  CaretRight,
  Shield,
  CurrencyDollar,
  ChartLineUp,
  Bank,
  Coins,
  Sparkle,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Toaster, toast } from "sonner";

const LOGO_URL = "/logo.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Section = ({ id, children, className = "", testId }) => (
  <section
    id={id}
    data-testid={testId}
    className={`relative w-full py-16 sm:py-20 lg:py-32 ${className}`}
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-8">{children}</div>
  </section>
);

// ---------- Speed-lines accent (mirrors the angled dashes in the logo) ----------
const SpeedLines = ({ className = "" }) => (
  <svg
    width="22"
    height="14"
    viewBox="0 0 22 14"
    fill="none"
    aria-hidden="true"
    className={className}
  >
    <path d="M2 11 L10 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" />
    <path d="M6 13 L14 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" opacity="0.7" />
    <path d="M10 14 L18 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" opacity="0.45" />
  </svg>
);

const Overline = ({ children }) => (
  <span className="inline-flex items-center gap-2.5 font-mark text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
    <SpeedLines className="text-[#1E3A6F]" />
    {children}
  </span>
);

// ---------- Animated Counter ----------
const Counter = ({ from = 0, to, suffix = "", duration = 1.6 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const mv = useMotionValue(from);
  const rounded = useTransform(mv, (v) => Math.round(v).toLocaleString());
  const [display, setDisplay] = useState(String(from));
  useEffect(() => {
    const unsub = rounded.on("change", (v) => setDisplay(v));
    return () => unsub();
  }, [rounded]);
  useEffect(() => {
    if (inView) {
      const controls = animate(mv, to, { duration, ease: [0.22, 1, 0.36, 1] });
      return () => controls.stop();
    }
  }, [inView, mv, to, duration]);
  return (
    <span ref={ref} className="tabular-nums">
      {display}
      {suffix}
    </span>
  );
};

// ---------- Site-wide ambient Payment Flow background ----------
const PaymentFlowBackground = () => {
  // Pool of curved Bezier paths that meander down the viewport
  const paths = [
    "M-60 120 C 200 80, 420 260, 720 180 S 1200 140, 1520 240",
    "M-60 320 C 260 380, 520 220, 820 340 S 1280 420, 1520 320",
    "M-60 520 C 180 460, 460 620, 780 520 S 1240 460, 1520 560",
    "M-60 740 C 220 820, 520 660, 820 760 S 1240 820, 1520 720",
    "M-60 940 C 300 880, 560 1060, 860 960 S 1260 900, 1520 980",
    "M-60 1140 C 240 1220, 540 1080, 820 1180 S 1260 1240, 1520 1140",
    "M-60 1340 C 280 1280, 580 1440, 880 1340 S 1280 1280, 1520 1380",
    "M-60 1540 C 220 1620, 520 1480, 820 1580 S 1240 1640, 1520 1540",
  ];
  return (
    <div
      data-testid="flow-background"
      aria-hidden="true"
      className="fixed inset-0 -z-10 pointer-events-none overflow-hidden motion-safe:block motion-reduce:hidden"
      style={{ height: "100vh" }}
    >
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1460 1720"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
      >
        <defs>
          <linearGradient id="flowStroke" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#1E3A6F" stopOpacity="0" />
            <stop offset="35%" stopColor="#1E3A6F" stopOpacity="0.22" />
            <stop offset="65%" stopColor="#2C4A8C" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#1E3A6F" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="particleGlow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#1E3A6F" stopOpacity="1" />
            <stop offset="60%" stopColor="#1E3A6F" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#1E3A6F" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Render each path + 2 particles travelling along it */}
        {paths.map((d, i) => {
          const pid = `flow-path-${i}`;
          const baseDur = 14 + (i % 4) * 3;
          return (
            <g key={pid}>
              <path
                id={pid}
                d={d}
                stroke="url(#flowStroke)"
                strokeWidth="1"
                strokeDasharray="2 6"
                fill="none"
                opacity="0.65"
              />
              <circle r="3" fill="url(#particleGlow)">
                <animateMotion
                  dur={`${baseDur}s`}
                  repeatCount="indefinite"
                  begin={`${i * 1.3}s`}
                  path={d}
                />
              </circle>
              <circle r="2" fill="#1E3A6F" opacity="0.55">
                <animateMotion
                  dur={`${baseDur + 4}s`}
                  repeatCount="indefinite"
                  begin={`${i * 1.3 + 5.5}s`}
                  path={d}
                />
              </circle>
              <circle r="1.4" fill="#2C4A8C" opacity="0.7">
                <animateMotion
                  dur={`${baseDur + 7}s`}
                  repeatCount="indefinite"
                  begin={`${i * 1.3 + 9}s`}
                  path={d}
                />
              </circle>
            </g>
          );
        })}
      </svg>

      {/* Soft vignettes so content stays readable */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(246,247,245,0.35) 0%, rgba(246,247,245,0.78) 70%, rgba(246,247,245,0.95) 100%)",
        }}
      />
    </div>
  );
};

// ---------- Hero Visual — Payments Flow Diagram ----------
const HeroVisual = () => {
  const inbound = [
    { id: "card", label: "Card / Wallet", icon: CreditCard, y: 80 },
    { id: "ach", label: "ACH / Bank", icon: Bank, y: 200 },
    { id: "giftin", label: "Gift / Prepaid", icon: Gift, y: 320 },
  ];
  const outbound = [
    { id: "payout-ach", label: "ACH", icon: Bank, y: 60 },
    { id: "payout-ptd", label: "Push to Debit", icon: CreditCard, y: 150 },
    { id: "payout-wallet", label: "Wallet", icon: Wallet, y: 240 },
    { id: "payout-card", label: "Card Program", icon: CreditCard, y: 330 },
  ];
  const CENTER = { x: 250, y: 220 };
  const LEFT_X = 70;
  const RIGHT_X = 430;

  // Build one path per in→center and center→out edge
  const inPaths = inbound.map((n) => ({
    id: `in-${n.id}`,
    d: `M${LEFT_X} ${n.y} C ${LEFT_X + 80} ${n.y}, ${CENTER.x - 90} ${CENTER.y}, ${CENTER.x - 28} ${CENTER.y}`,
  }));
  const outPaths = outbound.map((n) => ({
    id: `out-${n.id}`,
    d: `M${CENTER.x + 28} ${CENTER.y} C ${CENTER.x + 90} ${CENTER.y}, ${RIGHT_X - 80} ${n.y}, ${RIGHT_X} ${n.y}`,
  }));

  return (
    <div
      data-testid="hero-visual"
      className="relative w-full h-[500px] lg:h-[560px] hidden lg:block"
      aria-hidden="true"
    >
      {/* Ambient glows (soft navy wash on light) */}
      <div
        className="absolute -inset-10 -z-10 blur-3xl opacity-40"
        style={{
          background:
            "radial-gradient(circle at 50% 45%, rgba(30,58,111,0.25) 0%, transparent 55%), radial-gradient(circle at 20% 85%, rgba(44,74,140,0.15) 0%, transparent 50%)",
        }}
      />
      {/* Masked grid */}
      <div
        className="absolute inset-0 -z-10 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(rgba(30,58,111,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(30,58,111,0.12) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage:
            "radial-gradient(circle at center, black 35%, transparent 78%)",
          WebkitMaskImage:
            "radial-gradient(circle at center, black 35%, transparent 78%)",
        }}
      />

      {/* SVG flow diagram */}
      <svg
        viewBox="0 0 500 440"
        className="absolute inset-0 w-full h-full"
        fill="none"
      >
        <defs>
          <linearGradient id="pathGrad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#1E3A6F" stopOpacity="0.0" />
            <stop offset="50%" stopColor="#1E3A6F" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#2C4A8C" stopOpacity="0.55" />
          </linearGradient>
          <radialGradient id="coreGrad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#2C4A8C" stopOpacity="0.35" />
            <stop offset="55%" stopColor="#1E3A6F" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#1E3A6F" stopOpacity="0" />
          </radialGradient>
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Base paths (visible) */}
        {[...inPaths, ...outPaths].map((p) => (
          <path
            key={p.id}
            id={p.id}
            d={p.d}
            stroke="url(#pathGrad)"
            strokeWidth="1.1"
            strokeDasharray="2 4"
            fill="none"
          />
        ))}

        {/* Flowing particles */}
        {inPaths.map((p, i) => (
          <g key={`p-${p.id}`} filter="url(#softGlow)">
            <circle r="2.8" fill="#1E3A6F">
              <animateMotion
                dur={`${5 + i * 0.7}s`}
                repeatCount="indefinite"
                begin={`${i * 0.9}s`}
                path={p.d}
              />
            </circle>
            <circle r="1.6" fill="#2C4A8C" opacity="0.75">
              <animateMotion
                dur={`${6.5 + i * 0.7}s`}
                repeatCount="indefinite"
                begin={`${i * 0.9 + 2.4}s`}
                path={p.d}
              />
            </circle>
          </g>
        ))}
        {outPaths.map((p, i) => (
          <g key={`p-${p.id}`} filter="url(#softGlow)">
            <circle r="2.8" fill="#1E3A6F">
              <animateMotion
                dur={`${4.5 + i * 0.6}s`}
                repeatCount="indefinite"
                begin={`${i * 0.7 + 0.3}s`}
                path={p.d}
              />
            </circle>
            <circle r="1.5" fill="#2C4A8C" opacity="0.75">
              <animateMotion
                dur={`${6 + i * 0.6}s`}
                repeatCount="indefinite"
                begin={`${i * 0.7 + 2.2}s`}
                path={p.d}
              />
            </circle>
          </g>
        ))}

        {/* Orchestration core (logo-inspired CP disc) */}
        <g transform={`translate(${CENTER.x} ${CENTER.y})`}>
          <circle r="64" fill="url(#coreGrad)" opacity="1" />
          <circle
            r="28"
            fill="#0A0D14"
            stroke="#1E3A6F"
            strokeOpacity="0.9"
            strokeWidth="1.5"
          />
          <circle
            r="40"
            fill="none"
            stroke="#1E3A6F"
            strokeOpacity="0.55"
            strokeWidth="1"
            strokeDasharray="3 6"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0"
              to="360"
              dur="28s"
              repeatCount="indefinite"
            />
          </circle>
          <text
            textAnchor="middle"
            dy="5"
            fontFamily="Saira Condensed, sans-serif"
            fontSize="16"
            fontWeight="600"
            letterSpacing="2"
            fill="#C7D2DE"
          >
            CP
          </text>
        </g>

        {/* Node dots */}
        {inbound.map((n) => (
          <circle key={n.id} cx={LEFT_X} cy={n.y} r="4" fill="#1E3A6F" />
        ))}
        {outbound.map((n) => (
          <circle key={n.id} cx={RIGHT_X} cy={n.y} r="4" fill="#1E3A6F" />
        ))}
      </svg>

      {/* Inbound node labels — positioned to the RIGHT of the dot, inside the column,
          so they never bleed into the hero text column on mid-size desktops. */}
      <div className="absolute inset-0 pointer-events-none">
        {inbound.map((n) => {
          const Icon = n.icon;
          return (
            <div
              key={n.id}
              className="absolute flex items-center gap-2 px-2.5 py-1.5 rounded-sm border border-slate-200 bg-white/95 backdrop-blur text-[10px] uppercase tracking-[0.18em] text-slate-600 font-mark whitespace-nowrap shadow-sm"
              style={{
                left: `${(LEFT_X / 500) * 100 + 2.6}%`,
                top: `${(n.y / 440) * 100 - 3.5}%`,
                transform: "translate(0, -50%)",
              }}
            >
              <Icon size={11} weight="duotone" className="text-[#1E3A6F]" />
              {n.label}
            </div>
          );
        })}
        {outbound.map((n) => {
          const Icon = n.icon;
          return (
            <div
              key={n.id}
              className="absolute flex items-center gap-2 px-2.5 py-1.5 rounded-sm border border-slate-200 bg-white/95 backdrop-blur text-[10px] uppercase tracking-[0.18em] text-slate-600 font-mark whitespace-nowrap shadow-sm"
              style={{
                left: `${(RIGHT_X / 500) * 100 - 2.6}%`,
                top: `${(n.y / 440) * 100 - 3.5}%`,
                transform: "translate(-100%, -50%)",
              }}
            >
              <Icon size={11} weight="duotone" className="text-[#1E3A6F]" />
              {n.label}
            </div>
          );
        })}
      </div>

      {/* Unified metric + live bar (bottom) */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex items-stretch divide-x divide-slate-200 rounded-sm border border-slate-200 bg-white/95 backdrop-blur shadow-[0_20px_50px_-20px_rgba(30,58,111,0.7)]"
      >
        <div className="flex items-center gap-1.5 px-3 py-2.5">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          <span className="text-[9px] font-mark uppercase tracking-[0.22em] text-emerald-300">Live</span>
        </div>
        <div className="px-3 py-2.5">
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] text-slate-400 font-mark">
            <Shield size={10} weight="duotone" className="text-[#1E3A6F]" /> Auth
          </div>
          <div className="mt-0.5 font-display text-base font-light text-slate-900 tabular-nums leading-none">
            94.7<span className="text-slate-500 text-[11px]">%</span>
          </div>
        </div>
        <div className="px-3 py-2.5">
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] text-slate-400 font-mark">
            <CurrencyDollar size={10} weight="duotone" className="text-[#1E3A6F]" /> Cost
          </div>
          <div className="mt-0.5 font-display text-base font-light text-slate-900 tabular-nums leading-none">
            2.13<span className="text-slate-500 text-[11px]">%</span>
          </div>
        </div>
        <div className="px-3 py-2.5">
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] text-slate-400 font-mark">
            <ChartLineUp size={10} weight="duotone" className="text-[#1E3A6F]" /> Routed
          </div>
          <div className="mt-0.5 font-display text-base font-light text-slate-900 tabular-nums leading-none">
            $50,360
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ---------- Marquee strip ----------
const MarqueeStrip = () => {
  const items = [
    "Pay-ins",
    "Payouts",
    "Embedded Finance",
    "BaaS",
    "Card Programs",
    "Embedded Lending",
    "Gift Cards",
    "Stablecoins",
    "Acquiring",
    "Issuing",
    "Platform Payments",
    "PCI DSS",
    "Interchange",
    "ACH",
    "Wire",
    "RTP / FedNow",
    "Push to Debit",
    "USDC",
    "On-chain Payouts",
    "Authorization",
    "Reconciliation",
    "Issuer Selection",
    "Processor Strategy",
    "Agentic Commerce",
    "Wallets",
    "Prepaid",
    "Network Tokens",
    "Routing",
    "Crypto Rails",
  ];
  const row = [...items, ...items];
  return (
    <div
      data-testid="marquee-strip"
      className="relative border-y border-slate-200 bg-white overflow-hidden"
    >
      <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-slate-50 to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-slate-50 to-transparent z-10 pointer-events-none" />
      <div className="flex whitespace-nowrap py-6 marquee-track">
        {row.map((it, i) => (
          <div
            key={i}
            className="flex items-center gap-10 px-6 text-slate-400 font-display text-lg sm:text-xl tracking-tight font-light"
          >
            <Sparkle size={12} weight="fill" className="text-slate-600" />
            <span className="hover:text-slate-900 transition-colors">{it}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Stats Section ----------
const StatsSection = () => {
  const stats = [
    { num: 10, suffix: "+", label: "Years in payments & fintech" },
    { num: 4, suffix: "", label: "Core verticals operated" },
    { num: 100, suffix: "M+", label: "In structured deal volume" },
    { num: 1, suffix: ":1", label: "Senior, hands-on engagement" },
  ];
  return (
    <section
      data-testid="stats-section"
      className="relative border-t border-slate-200/60 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 lg:py-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 rounded-sm overflow-hidden">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`stat-${i}`}
              className="bg-white hover:bg-[#0F1320] transition-colors px-6 py-10 lg:px-10 lg:py-12"
            >
              <div className="font-display text-5xl lg:text-6xl font-light text-slate-900 leading-none">
                <Counter to={s.num} suffix={s.suffix} />
              </div>
              <div className="mt-4 h-px w-8 bg-blue-400/60" />
              <div className="mt-4 text-sm text-slate-400 font-light leading-snug max-w-[200px]">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};


// ---------- Navbar ----------
const Navbar = ({ onOpenForm }) => {
  const [open, setOpen] = useState(false);
  const links = [
    { id: "framework", label: "Framework" },
    { id: "services", label: "Engagement" },
    { id: "approach", label: "Approach" },
    { id: "about", label: "About" },
    { id: "faq", label: "FAQ" },
    { id: "contact", label: "Contact" },
  ];
  return (
    <header
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-[#F6F7F5]/80 backdrop-blur-xl border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 sm:h-24 flex items-center justify-between">
        <a
          href="#top"
          data-testid="navbar-logo"
          className="flex items-center gap-2 sm:gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt="Cody Payments Consulting"
            className="h-14 w-14 sm:h-20 sm:w-20 object-contain"
          />
          <span className="hidden sm:inline-flex items-center gap-2 px-2.5 py-1 border-l border-slate-300 pl-4 font-mark text-[10px] uppercase tracking-[0.32em] text-slate-500">
            Payments · Strategy
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={onOpenForm}
            data-testid="nav-cta-button"
            className="hidden sm:inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-[#1E3A6F] transition-all duration-200 px-5 py-2.5 rounded-sm text-sm font-medium hover:scale-[1.02]"
          >
            Get a Flow Control Review
            <ArrowRight size={16} weight="bold" />
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="md:hidden text-slate-900 p-2"
          >
            <Stack size={22} />
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-[#F6F7F5]/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                data-testid={`nav-link-mobile-${l.id}`}
                className="text-base text-slate-700"
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                onOpenForm();
              }}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-sm text-sm font-medium"
            >
              Get a Flow Control Review <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

// ---------- Hero ----------
const Hero = ({ onOpenForm }) => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative w-full pt-24 pb-14 sm:pt-36 sm:pb-20 lg:pt-44 lg:pb-28 overflow-hidden min-h-[680px] sm:min-h-[720px] lg:min-h-[780px]"
  >
    {/* Navy aurora (lighter on light theme) */}
    <div
      className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] -z-10 opacity-20 blur-3xl pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(30,58,111,0.45) 0%, transparent 60%)",
      }}
    />
    {/* Subtle masked grid */}
    <div
      className="absolute inset-0 -z-10 opacity-[0.08] pointer-events-none"
      style={{
        backgroundImage:
          "linear-gradient(rgba(30,58,111,1) 1px, transparent 1px), linear-gradient(90deg, rgba(30,58,111,1) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
        maskImage:
          "radial-gradient(ellipse at 30% 40%, black 0%, transparent 70%)",
        WebkitMaskImage:
          "radial-gradient(ellipse at 30% 40%, black 0%, transparent 70%)",
      }}
    />
    <div className="absolute inset-0 -z-10 pointer-events-none" />

    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial="hidden"
            animate="visible"
            custom={0}
            variants={fadeUp}
          >
            <Overline>An Advisory Firm · Payments Infrastructure</Overline>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeUp}
            data-testid="hero-headline"
            className="font-display mt-5 sm:mt-6 text-[40px] leading-[1.05] sm:text-5xl lg:text-[64px] font-light tracking-tight text-slate-900"
          >
            Turn payments infrastructure into{" "}
            <span className="font-mark font-semibold text-[#1E3A6F]">
              operating leverage
            </span>
            .
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            custom={2}
            variants={fadeUp}
            className="mt-5 sm:mt-7 text-sm sm:text-base lg:text-lg font-light text-slate-600 max-w-xl leading-relaxed"
          >
            <span className="font-semibold text-slate-900">The Flow Control Framework™</span> is the method — applied through{" "}
            <span className="text-slate-900">fractional leadership</span>,{" "}
            <span className="text-slate-900">diagnostic reviews</span>, and{" "}
            <span className="text-slate-900">scoped execution</span>. For fintech and platforms scaling past $100M in payment volume.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            custom={3}
            variants={fadeUp}
            className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <button
              onClick={onOpenForm}
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 bg-slate-900 text-white hover:bg-[#1E3A6F] transition-all duration-200 px-6 sm:px-7 py-3.5 sm:py-4 rounded-sm text-sm sm:text-base font-medium hover:scale-[1.02]"
            >
              Get a Flow Control Review
              <ArrowRight
                size={18}
                weight="bold"
                className="group-hover:translate-x-0.5 transition-transform"
              />
            </button>
            <a
              href="#framework"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-slate-900 border border-slate-300 hover:border-slate-500 hover:bg-slate-200 transition-all duration-200 px-6 sm:px-7 py-3.5 sm:py-4 rounded-sm text-sm sm:text-base font-medium"
            >
              View the Framework
              <CaretRight size={18} weight="bold" />
            </a>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            custom={4}
            variants={fadeUp}
            className="mt-4 flex items-center gap-2 text-[11px] sm:text-xs text-slate-500 font-light"
          >
            <CheckCircle size={14} weight="fill" className="text-emerald-600 flex-none" />
            <span>
              Free 20-min call · No obligation · Scoped proposal within a week
            </span>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            custom={5}
            variants={fadeUp}
            className="mt-10 sm:mt-12 flex items-center gap-3"
          >
            <div className="h-px w-10 bg-slate-300" />
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.28em] text-slate-400">
              Principal-led · Limited engagements
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <HeroVisual />
        </div>
      </div>
    </div>
  </section>
);

// ---------- Reframe: Most payment problems are not technical ----------
const ProblemSection = () => {
  const misalignments = [
    {
      num: "01",
      pillar: "Flow Design",
      symptom: "Teams debate vendors",
      truth: "The flow was never defined.",
    },
    {
      num: "02",
      pillar: "Control Layer",
      symptom: "Decisions stall",
      truth: "No one owns the decision.",
    },
    {
      num: "03",
      pillar: "Partner Architecture",
      symptom: "Processors get blamed",
      truth: "Responsibilities overlap.",
    },
    {
      num: "04",
      pillar: "Economic Engine",
      symptom: "Cost surprises land quarterly",
      truth: "The economics aren't modeled.",
    },
    {
      num: "05",
      pillar: "Execution Integrity",
      symptom: "Auth rates drift",
      truth: "There's no integrity layer.",
    },
    {
      num: "06",
      pillar: "Scale Readiness",
      symptom: "Growth breaks the stack",
      truth: "It was never built for it.",
    },
  ];
  return (
    <Section testId="problem-section" className="border-t border-slate-200/60">
      <div className="max-w-5xl">
        <Overline>The Reframe</Overline>
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7 }}
          className="font-display mt-5 text-3xl sm:text-4xl lg:text-[56px] font-light tracking-tight text-slate-900 leading-[1.05] max-w-4xl"
        >
          Most payment problems{" "}
          <span className="brand-emphasis text-[#1E3A6F]">are not technical</span>.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-6 sm:mt-8 text-base sm:text-lg font-light text-slate-600 leading-relaxed max-w-3xl"
        >
          They come from{" "}
          <span className="font-semibold text-slate-900">
            flow, control, partners, economics, execution, and scale
          </span>{" "}
          being misaligned. The symptom is technical. The cause is structural.
          That's why adding another vendor doesn't fix it.
        </motion.p>
      </div>

      <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        {misalignments.map((m, i) => (
          <motion.div
            key={m.num}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.45, delay: i * 0.06 }}
            data-testid={`reframe-item-${i}`}
            className="bg-white p-6 sm:p-7 flex flex-col"
          >
            <div className="flex items-baseline gap-3">
              <span className="font-display text-xs text-[#1E3A6F] tabular-nums tracking-[0.18em]">
                {m.num}
              </span>
              <span className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500">
                {m.pillar}
              </span>
            </div>
            <div className="mt-4 flex items-start gap-3">
              <span className="font-display text-sm text-slate-400 italic line-through decoration-slate-300 leading-snug">
                {m.symptom}
              </span>
            </div>
            <div className="mt-2 flex items-start gap-2">
              <ArrowRight size={14} weight="bold" className="text-[#1E3A6F] mt-1.5 flex-none" />
              <span className="font-display text-base sm:text-lg text-slate-900 font-medium leading-snug">
                {m.truth}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-12 sm:mt-16 pt-8 border-t border-slate-200 max-w-3xl">
        <div className="font-mark text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-4">
          The answer
        </div>
        <p className="text-base sm:text-lg text-slate-900 font-light leading-relaxed">
          Not another vendor. Not another deck. A system.{" "}
          <span className="font-semibold">The Flow Control Framework™</span> —
          six diagnostic lenses that name the real problem and resolve it.
        </p>
        <a
          href="#framework"
          data-testid="reframe-cta"
          className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-slate-900 border-b border-slate-900 hover:gap-3 transition-all"
        >
          See how I fix payments
          <ArrowRight size={14} weight="bold" />
        </a>
      </div>
    </Section>
  );
};

// ---------- Selected Experience — Proof ----------
const EXPERIENCE_ITEMS = [
  { icon: CreditCard, title: "Enterprise card programs", note: "Prepaid, debit, and commercial" },
  { icon: Wallet, title: "Cash disbursement infrastructure", note: "ACH · RTP · FedNow · push-to-debit · wire" },
  { icon: Gift, title: "Gift & prepaid ecosystems", note: "Distribution, partner networks, interchange" },
  { icon: Stack, title: "Embedded finance", note: "BaaS, embedded accounts, sponsor-bank stacks" },
  { icon: Handshake, title: "Sponsor bank relationships", note: "Selection, negotiation, program governance" },
  { icon: ArrowsLeftRight, title: "Processor selection & negotiation", note: "Acquirer / issuer / gateway RFPs" },
  { icon: Lightning, title: "GTM strategy for payments", note: "Positioning, pricing, launch sequencing" },
  { icon: Sparkle, title: "Partnership development", note: "Networks, orchestrators, and vendors" },
  { icon: CurrencyDollar, title: "Commercial payments", note: "B2B, virtual card, receivables, treasury" },
];

const SelectedExperienceSection = () => (
  <Section testId="experience-section" className="border-t border-slate-200/60">
    <div className="max-w-4xl">
      <Overline>Selected Experience</Overline>
      <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
        What I&apos;ve owned.{" "}
        <span className="brand-emphasis text-[#1E3A6F]">What I&apos;ve shipped.</span>
      </h2>
      <p className="mt-6 sm:mt-8 text-base sm:text-lg font-light text-slate-600 leading-relaxed max-w-3xl">
        Ten-plus years operating payments at fintech, platform, and enterprise scale.
        Negotiated enterprise partnerships. Managed millions in payment volume. Launched
        programs from zero. This is what I bring on day one.
      </p>
    </div>

    <div className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
      {EXPERIENCE_ITEMS.map((item, i) => {
        const Icon = item.icon;
        return (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.4, delay: i * 0.045 }}
            data-testid={`experience-item-${i}`}
            className="bg-white p-5 sm:p-6 flex items-start gap-4 hover:bg-slate-50 transition-colors"
          >
            <div className="flex-none w-9 h-9 rounded-sm border border-slate-200 bg-slate-50 flex items-center justify-center">
              <Icon size={16} weight="duotone" className="text-[#1E3A6F]" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-sm sm:text-base font-medium text-slate-900 leading-snug">
                {item.title}
              </div>
              <div className="mt-1 text-[11px] sm:text-xs text-slate-500 font-light leading-snug">
                {item.note}
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>

    <div className="mt-10 sm:mt-14 max-w-3xl">
      <div className="grid grid-cols-3 gap-6 sm:gap-10">
        <div>
          <div className="font-display text-3xl sm:text-4xl font-light text-[#1E3A6F] tabular-nums">10+</div>
          <div className="mt-1 font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500">Years in payments</div>
        </div>
        <div>
          <div className="font-display text-3xl sm:text-4xl font-light text-[#1E3A6F] tabular-nums">60+</div>
          <div className="mt-1 font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500">Payment systems</div>
        </div>
        <div>
          <div className="font-display text-3xl sm:text-4xl font-light text-[#1E3A6F] tabular-nums">$B+</div>
          <div className="mt-1 font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500">Volume managed</div>
        </div>
      </div>
    </div>
  </Section>
);

// ---------- The Flow Control Framework ----------
const FRAMEWORK_PILLARS = [
  {
    num: "01",
    title: "Flow Design",
    icon: ArrowsLeftRight,
    subtitle: "Diagnostic lens — how money is designed to move",
    check: "Are all four fund flows — initiation, settlement, exception, and reversal — explicitly defined, owned, and reconciled?",
    points: [
      "Payment types (pay-ins, payouts, cards, stored value)",
      "End-to-end flow from initiation to settlement",
      "Alignment to business model and use case",
    ],
  },
  {
    num: "02",
    title: "Control Layer",
    icon: Shield,
    subtitle: "Diagnostic lens — who owns what, when",
    check: "Does every payment decision have a single named owner, an approval threshold, and a documented exception path?",
    points: [
      "Roles and responsibilities across teams",
      "Approval logic and decision points",
      "Exception handling and accountability",
    ],
  },
  {
    num: "03",
    title: "Partner Architecture",
    icon: Handshake,
    subtitle: "Diagnostic lens — external ecosystem alignment",
    check: "Are processor, issuer, bank, and network responsibilities cleanly separated — with zero functional overlap?",
    points: [
      "Processors, issuers, banks, networks",
      "Clear separation of responsibilities",
      "Elimination of overlap and unnecessary complexity",
    ],
  },
  {
    num: "04",
    title: "Economic Engine",
    icon: CurrencyDollar,
    subtitle: "Diagnostic lens — where money is made or lost",
    check: "Is unit economics modeled per rail, per partner, and per transaction type — with margin defensible to the board?",
    points: [
      "Fee structures and cost per transaction",
      "Interchange, revenue share, and incentives",
      "Pricing alignment across partners",
    ],
  },
  {
    num: "05",
    title: "Execution Integrity",
    icon: Gauge,
    subtitle: "Diagnostic lens — what holds under real volume",
    check: "Are authorization, reconciliation, and exception handling instrumented with monitored thresholds, not retrospective dashboards?",
    points: [
      "Payment success rates and delivery performance",
      "Reconciliation and reporting accuracy",
      "Operational consistency under volume",
    ],
  },
  {
    num: "06",
    title: "Scale Readiness",
    icon: TrendUp,
    subtitle: "Diagnostic lens — what breaks before it should",
    check: "Has the system been stress-tested at 3× current volume across rails, partners, and operational workflows?",
    points: [
      "Volume stress points and bottlenecks",
      "Workflow durability",
      "Long-term flexibility without rework",
    ],
  },
];

const FrameworkOrbital = () => {
  // 6 points arranged around a circle (top, then clockwise at 60° intervals)
  const R = 200;
  const CX = 260;
  const CY = 260;
  const positions = [0, 60, 120, 180, 240, 300].map((deg) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return { x: CX + R * Math.cos(rad), y: CY + R * Math.sin(rad) };
  });
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? FRAMEWORK_PILLARS[activeIdx] : null;

  // Cycle through pillars on first-view (teaches interactivity); pauses on hover/click
  const [autoCycle, setAutoCycle] = useState(true);
  useEffect(() => {
    if (!autoCycle) return;
    let i = 0;
    const id = setInterval(() => {
      setActiveIdx(i % 6);
      i += 1;
      if (i > 12) {
        clearInterval(id);
        setActiveIdx(null);
      }
    }, 1400);
    return () => clearInterval(id);
  }, [autoCycle]);

  const handleActivate = (i) => {
    setAutoCycle(false);
    setActiveIdx(i);
  };

  return (
    <div
      className="relative w-full max-w-[560px] mx-auto aspect-square"
      data-testid="framework-orbital"
      onMouseLeave={() => {
        // keep selection after mouse leaves — click to dismiss
      }}
    >
      <svg viewBox="0 0 520 520" className="absolute inset-0 w-full h-full" fill="none">
        <defs>
          <radialGradient id="fcfCore" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#2C4A8C" stopOpacity="0.4" />
            <stop offset="60%" stopColor="#1E3A6F" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#1E3A6F" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="fcfSpoke" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#1E3A6F" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#1E3A6F" stopOpacity="0.15" />
          </linearGradient>
        </defs>

        {/* Outer dashed orbit */}
        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="none"
          stroke="#1E3A6F"
          strokeOpacity="0.22"
          strokeWidth="1"
          strokeDasharray="3 6"
        />

        {/* Spokes — highlighted when pillar is active */}
        {positions.map((p, i) => (
          <line
            key={`spoke-${i}`}
            x1={CX}
            y1={CY}
            x2={p.x}
            y2={p.y}
            stroke={activeIdx === i ? "#1E3A6F" : "url(#fcfSpoke)"}
            strokeWidth={activeIdx === i ? "2" : "1"}
            opacity={activeIdx === null || activeIdx === i ? 1 : 0.35}
            style={{ transition: "all 0.4s ease" }}
          />
        ))}

        {/* Core glow */}
        <circle cx={CX} cy={CY} r="90" fill="url(#fcfCore)" />
        {/* Inner rotating ring */}
        <circle
          cx={CX}
          cy={CY}
          r="56"
          fill="none"
          stroke="#1E3A6F"
          strokeOpacity="0.45"
          strokeWidth="1"
          strokeDasharray="3 6"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from={`0 ${CX} ${CY}`}
            to={`360 ${CX} ${CY}`}
            dur="30s"
            repeatCount="indefinite"
          />
        </circle>
        {/* Solid inner disk */}
        <circle cx={CX} cy={CY} r="48" fill="#0A0D14" stroke="#1E3A6F" strokeWidth="1.5" />

        {/* Pillar nodes — interactive */}
        {positions.map((p, i) => {
          const isActive = activeIdx === i;
          const isDim = activeIdx !== null && !isActive;
          return (
            <g
              key={`node-${i}`}
              onClick={() => handleActivate(i)}
              onMouseEnter={() => {
                setAutoCycle(false);
                setActiveIdx(i);
              }}
              style={{ cursor: "pointer", transition: "all 0.35s ease" }}
              data-testid={`orbital-node-${FRAMEWORK_PILLARS[i].num}`}
            >
              {/* Hover ring */}
              {isActive && (
                <circle
                  cx={p.x}
                  cy={p.y}
                  r="34"
                  fill="none"
                  stroke="#1E3A6F"
                  strokeOpacity="0.2"
                  strokeWidth="8"
                />
              )}
              <circle
                cx={p.x}
                cy={p.y}
                r="26"
                fill={isActive ? "#1E3A6F" : "#ffffff"}
                stroke="#1E3A6F"
                strokeWidth={isActive ? "2" : "1.5"}
                opacity={isDim ? 0.5 : 1}
              />
              <text
                x={p.x}
                y={p.y + 4}
                textAnchor="middle"
                fontFamily="Saira Condensed, sans-serif"
                fontSize="14"
                fontWeight="600"
                letterSpacing="1"
                fill={isActive ? "#ffffff" : "#1E3A6F"}
                opacity={isDim ? 0.6 : 1}
                style={{ pointerEvents: "none" }}
              >
                {FRAMEWORK_PILLARS[i].num}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Center label (HTML overlay for crisp typography + animated state change) */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="flex flex-col items-center justify-center text-center"
          style={{
            width: "22%",
            height: "22%",
          }}
        >
          {active ? (
            <motion.div
              key={active.num}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center"
            >
              <div className="font-display text-[10px] sm:text-[11px] text-[#8FA3BF] tabular-nums tracking-[0.2em]">
                {active.num}
              </div>
              <div className="mt-0.5 font-mark text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.14em] text-white leading-tight max-w-[120px]">
                {active.title}
              </div>
            </motion.div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="font-mark text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C7D2DE] leading-tight">
                Flow
              </div>
              <div className="mt-0.5 font-mark text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C7D2DE] leading-tight">
                Control
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Desktop: pillar labels around orbital — hidden when a node is active (replaced by center label) */}
      {FRAMEWORK_PILLARS.map((p, i) => {
        const pos = positions[i];
        const dx = pos.x - CX;
        const dy = pos.y - CY;
        const len = Math.sqrt(dx * dx + dy * dy);
        const OFFSET = 46;
        const lx = pos.x + (dx / len) * OFFSET;
        const ly = pos.y + (dy / len) * OFFSET;
        const align =
          lx < CX - 10 ? "right" : lx > CX + 10 ? "left" : "center";
        const isActive = activeIdx === i;
        const isDim = activeIdx !== null && !isActive;
        return (
          <div
            key={p.num}
            className={`hidden lg:block absolute font-mark text-[11px] uppercase tracking-[0.22em] whitespace-nowrap pointer-events-none transition-all duration-300 ${
              isActive ? "text-[#1E3A6F] font-semibold" : "text-slate-900"
            }`}
            style={{
              left: `${(lx / 520) * 100}%`,
              top: `${(ly / 520) * 100}%`,
              opacity: isDim ? 0.35 : 1,
              transform: `translate(${
                align === "right" ? "-100%" : align === "center" ? "-50%" : "0"
              }, -50%)`,
            }}
          >
            {p.title}
          </div>
        );
      })}

      {/* Description strip below orbital (active pillar) */}
      <div
        className="absolute left-0 right-0 -bottom-4 sm:-bottom-2 px-2 text-center pointer-events-none"
        aria-live="polite"
      >
        {active ? (
          <motion.div
            key={`desc-${active.num}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="inline-flex flex-col items-center gap-0.5 bg-white/90 backdrop-blur border border-slate-200 rounded-sm px-3 py-1.5 sm:px-4 sm:py-2 shadow-sm"
            data-testid="orbital-active-label"
          >
            <span className="font-display text-[11px] sm:text-xs font-semibold text-[#1E3A6F] tracking-[0.1em]">
              {active.num} · {active.title}
            </span>
            <span className="text-[10px] sm:text-[11px] text-slate-600 font-light italic">
              {active.subtitle}
            </span>
          </motion.div>
        ) : (
          <div className="inline-flex items-center gap-2 font-mark text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-slate-500">
            <span className="block w-1.5 h-1.5 rounded-full bg-[#1E3A6F] animate-pulse" />
            Tap a node to explore the pillar
          </div>
        )}
      </div>
    </div>
  );
};

const FlowControlFramework = () => {
  return (
    <Section id="framework" testId="framework-section" className="border-t border-slate-200/60">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <Overline>How I Fix Payments</Overline>
          <div
            className="mt-5 sm:mt-6 font-display text-sm sm:text-base text-[#1E3A6F] font-medium uppercase tracking-[0.12em]"
            data-testid="framework-proprietary-statement"
          >
            The Flow Control Framework™ is how I evaluate and fix payment systems.
          </div>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            The Flow Control{" "}
            <span className="brand-emphasis text-[#1E3A6F]">Framework</span>
            <sup className="font-mark text-sm align-top ml-1 text-slate-500">™</sup>
          </h2>
          <p
            className="mt-6 sm:mt-8 text-base sm:text-lg font-light text-slate-700 leading-relaxed"
            data-testid="framework-etymology"
          >
            Built on the two foundations of any payment system: how money moves
            (<span className="font-semibold text-slate-900">Flow</span>) and who
            owns each step (<span className="font-semibold text-slate-900">Control</span>).
            Four more lenses extend from there.
          </p>
          <p className="mt-4 text-sm sm:text-base font-light text-slate-600 leading-relaxed">
            Six diagnostic lenses. The same lens, every time. I use it on every
            engagement — a card program, an embedded finance stack, a stablecoin
            payout rail, an agentic-commerce integration — all diagnosed and
            rebuilt through the same six pillars. No bespoke method per project.
            No vendor-of-the-week. One framework.
          </p>
          <div
            className="mt-6 sm:mt-8 inline-flex items-start gap-3 px-4 py-3 rounded-sm border border-slate-200 bg-white max-w-md"
            data-testid="framework-synthesis"
          >
            <span className="font-display text-[10px] uppercase tracking-[0.24em] text-slate-500 font-mark mt-0.5 flex-none">
              Synthesis
            </span>
            <span className="text-xs sm:text-sm text-slate-700 font-light leading-snug">
              Refined across 60+ payment systems and a decade of operator
              engagements — BaaS, card programs, payouts, and emerging rails.
            </span>
          </div>
          <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-slate-200">
            <div className="font-mark text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-3">
              The result
            </div>
            <p className="text-sm sm:text-base text-slate-900 font-light leading-relaxed">
              A payment system that is{" "}
              <span className="font-semibold">structured, controlled,
              cost-efficient</span>, and built to{" "}
              <span className="font-semibold">scale without breaking</span>.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <FrameworkOrbital />
        </div>
      </div>

      {/* Pillar detail cards */}
      <div className="mt-20 pt-12 border-t border-slate-200">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <Overline>Six Pillars</Overline>
            <h3 className="font-display mt-4 text-2xl sm:text-3xl font-light tracking-tight text-slate-900">
              One framework. Every payment decision.
            </h3>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200">
          {FRAMEWORK_PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                data-testid={`framework-pillar-${p.num}`}
                className="group bg-white hover:bg-slate-50 transition-colors duration-300 p-7 sm:p-8 flex flex-col"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-sm border border-slate-200 bg-slate-50 flex items-center justify-center group-hover:border-[#1E3A6F]/40 group-hover:bg-white transition-colors">
                    <Icon size={20} weight="duotone" className="text-[#1E3A6F]" />
                  </div>
                  <span className="font-display text-sm tracking-[0.18em] text-[#1E3A6F] tabular-nums">
                    {p.num}
                  </span>
                </div>
                <h4 className="font-display text-xl sm:text-2xl font-medium text-slate-900">
                  {p.title}
                </h4>
                <p className="mt-2 text-sm text-slate-500 font-light italic">
                  {p.subtitle}
                </p>
                {p.check && (
                  <div className="mt-4 rounded-sm border border-[#1E3A6F]/15 bg-[#1E3A6F]/5 px-3 py-2.5">
                    <div className="font-mark text-[9px] uppercase tracking-[0.22em] text-[#1E3A6F] mb-1">
                      Diagnostic check
                    </div>
                    <p className="text-[13px] sm:text-sm text-slate-800 font-medium leading-snug">
                      {p.check}
                    </p>
                  </div>
                )}
                <ul className="mt-5 space-y-2.5 pt-4 border-t border-slate-100">
                  {p.points.map((pt) => (
                    <li
                      key={pt}
                      className="flex items-start gap-2.5 text-sm text-slate-700 font-light leading-snug"
                    >
                      <span className="block w-1 h-1 rounded-full bg-[#1E3A6F] mt-2 flex-none" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </Section>
  );
};


// ---------- Methodology — Input → Method → Output ----------
const MethodologySection = () => {
  const stages = [
    {
      label: "Input",
      heading: "What goes in",
      items: [
        "Payment system architecture",
        "Vendor and processor contracts",
        "Volume, mix, and rail data",
        "Operating model and team ownership",
      ],
    },
    {
      label: "Method",
      heading: "How it's evaluated",
      items: [
        "Six-pillar diagnostic framework",
        "Each pillar scored: Clear / Risk / Gap",
        "Scored against operator-grade benchmarks",
        "Cross-pillar dependency mapping",
      ],
    },
    {
      label: "Output",
      heading: "What you receive",
      items: [
        "Pillar-by-pillar readiness scores",
        "Named gaps ranked by risk",
        "Prioritized roadmap with quantified impact",
        "30 · 60 · 90 day execution plan",
      ],
    },
  ];
  return (
    <Section testId="methodology-section" className="border-t border-slate-200/60">
      <div className="max-w-4xl">
        <Overline>Methodology</Overline>
        <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
          Same inputs.{" "}
          <span className="brand-emphasis text-[#1E3A6F]">Same outputs. Every time.</span>
        </h2>
        <p className="mt-6 sm:mt-8 text-base sm:text-lg font-light text-slate-600 leading-relaxed max-w-3xl">
          The framework is a method, not an opinion. Three stages — Input, Method,
          Output — applied identically across every engagement. Repeatable by
          design. Defensible to a board. The Scorecard above is this method,
          rendered live.
        </p>
      </div>

      <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        {stages.map((s, idx) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: idx * 0.1 }}
            data-testid={`methodology-stage-${idx}`}
            className="relative bg-white p-6 sm:p-8 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <span className="font-display text-xs text-[#1E3A6F] tabular-nums tracking-[0.2em]">
                0{idx + 1}
              </span>
              <span className="font-mark text-[10px] uppercase tracking-[0.24em] text-[#1E3A6F] font-semibold">
                {s.label}
              </span>
            </div>
            <h3 className="mt-4 font-display text-xl sm:text-2xl font-medium text-slate-900 leading-snug">
              {s.heading}
            </h3>
            <ul className="mt-5 space-y-2.5 pt-4 border-t border-slate-100">
              {s.items.map((it) => (
                <li
                  key={it}
                  className="flex items-start gap-2.5 text-sm text-slate-700 font-light leading-snug"
                >
                  <span className="block w-1 h-1 rounded-full bg-[#1E3A6F] mt-2 flex-none" />
                  {it}
                </li>
              ))}
            </ul>
            {/* Connector arrow between stages on desktop */}
            {idx < stages.length - 1 && (
              <div className="hidden md:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 items-center justify-center w-6 h-6 rounded-full bg-white border border-slate-200">
                <ArrowRight size={12} weight="bold" className="text-[#1E3A6F]" />
              </div>
            )}
          </motion.div>
        ))}
      </div>

      <div className="mt-10 sm:mt-12 max-w-3xl text-sm text-slate-600 font-light leading-relaxed">
        Repeatable. Auditable. Operator-grade. The same method that runs the{" "}
        <a
          href="#about"
          className="border-b border-slate-400 hover:border-slate-900 hover:text-slate-900 transition-colors"
        >
          Framework Readiness Scorecard
        </a>{" "}
        runs every engagement.
      </div>
    </Section>
  );
};

// ---------- Services (The Engagement) ----------
const ServicesSection = () => {
  const engagements = [
    {
      testId: "engagement-fractional",
      num: "01",
      tag: "Flagship · Monthly retainer",
      verb: "I own",
      title: "Fractional Head of Payments",
      featured: true,
      desc: "I take ownership of your payments function — end to end. Card programs, sponsor banks, processors, payouts & disbursements, embedded finance, and the Flow Control Framework™ as your operating model. For companies between hires, pre-hire, or growing past the point where payments can be a side-of-desk function.",
      bullets: [
        "A decision-maker for payments on day one",
        "Vendors, partners, and teams accountable to one owner",
        "Board-ready payments narrative every quarter",
        "Continuity through the hiring gap — no scramble",
      ],
    },
    {
      testId: "engagement-review",
      num: "02",
      tag: "Core · 4–6 weeks",
      verb: "I diagnose",
      title: "The Flow Control Review",
      desc: "I run your pay-ins and payouts through all six pillars of the Flow Control Framework™. You get a prioritized roadmap with quantified impact. This is the entry point most engagements start with.",
      bullets: [
        "Every pillar of your stack scored — Clear, Risk, or Gap",
        "Dollar impact quantified per finding",
        "A roadmap you can hand to the board and the team",
        "A defensible 30 · 60 · 90 plan with named ownership",
      ],
      cta: { label: "View sample deliverable", to: "/sample" },
    },
    {
      testId: "engagement-execution",
      num: "03",
      tag: "Follow-on · Scoped",
      verb: "I execute",
      title: "Make It Work",
      desc: "I apply the framework to a specific initiative — a new rail, vendor migration, processor renegotiation, or product launch. Hands-on, scoped to the pillars that move the work. I ensure it ships and it ships correctly.",
      bullets: [
        "The initiative ships — with the pillars that matter locked down",
        "Contracts renegotiated. Real dollars back on the P&L",
        "Migration, routing, and tokenization decisions made and executed",
        "Your team stays the owner. I'm the force multiplier",
      ],
    },
  ];

  const domains = [
    {
      icon: ArrowsLeftRight,
      title: "Pay-ins",
      side: "Acquiring",
      summary: "Processor, gateway, acquirer, auth rate, 3DS, tokenization, PCI DSS scope.",
    },
    {
      icon: Wallet,
      title: "Payouts & Disbursements",
      side: "Disbursement",
      summary: "ACH, RTP, FedNow, push-to-debit, wire, wallets, on-chain. Rail mix, vendor design, cost arbitrage.",
    },
    {
      icon: Stack,
      title: "Embedded Finance",
      side: "BaaS · Platforms",
      summary: "Sponsor-bank selection, BaaS stack, embedded payments, lending, and accounts inside your product.",
    },
    {
      icon: CreditCard,
      title: "Card Programs",
      side: "Issuing",
      summary: "Prepaid, debit, credit. Issuer, BIN sponsor, processor selection, program economics.",
    },
    {
      icon: Gift,
      title: "Gift Cards",
      side: "Issuing",
      summary: "Partner ecosystem, distribution, interchange structure, growth strategy.",
    },
    {
      icon: Coins,
      title: "Stablecoins",
      side: "Acquiring + Issuing",
      summary: "On-chain pay-ins and payouts, rail selection, treasury, compliance.",
    },
  ];

  return (
    <Section id="services" testId="services-section" className="border-t border-slate-200/60">
      <div className="max-w-3xl">
        <Overline>How To Engage</Overline>
        <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-[1.1]">
          Own it. Diagnose it. Ship it.{" "}
          <span className="brand-emphasis text-[#1E3A6F]">One of three ways.</span>
        </h2>
        <div className="mt-8 flex items-start gap-4 pt-6 border-t border-slate-200 max-w-2xl">
          <SpeedLines className="text-[#2C4A8C] mt-2 flex-none" />
          <p className="text-lg sm:text-xl font-light text-slate-700 leading-relaxed">
            Most engagements start with a{" "}
            <span className="text-slate-900 font-normal">Review</span> or scoped{" "}
            <span className="text-slate-900 font-normal">execution</span>. The{" "}
            <span className="text-slate-900 font-normal">Fractional Head of Payments</span>{" "}
            retainer is the flagship — full ownership, monthly.
          </p>
        </div>
      </div>

      {/* Engagement tier cards */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200">
        {engagements.map((e, i) => (
          <motion.div
            key={e.testId}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            custom={i}
            variants={fadeUp}
            data-testid={e.testId}
            className={`group relative bg-white hover:bg-slate-50 transition-all duration-500 p-8 lg:p-10 flex flex-col overflow-hidden ${
              e.featured ? "lg:border-t-4 lg:border-t-[#1E3A6F] ring-1 ring-[#1E3A6F]/10" : (i === 0 ? "lg:border-t-2 lg:border-t-[#1E3A6F]" : "")
            }`}
          >
            {/* Flagship badge */}
            {e.featured && (
              <div className="absolute top-4 right-4 z-10">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-[#1E3A6F] text-white font-mark text-[9px] uppercase tracking-[0.22em] font-semibold">
                  <Sparkle size={9} weight="fill" /> Flagship
                </span>
              </div>
            )}
            {/* Accent sweep on hover */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#1E3A6F]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div
              className="absolute -top-24 -right-24 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl pointer-events-none"
              style={{
                background: "radial-gradient(circle, rgba(30,58,111,0.35) 0%, transparent 70%)",
              }}
            />

            <div className="relative flex items-start justify-between mb-6">
              <span className="font-display text-xs tracking-[0.24em] text-[#1E3A6F] tabular-nums">
                {e.num}
              </span>
              <span className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500">
                {e.tag}
              </span>
            </div>

            <h3 className="relative font-display text-2xl sm:text-3xl font-medium text-slate-900 mb-2 group-hover:translate-x-0.5 transition-transform duration-500">
              {e.title}
            </h3>
            <div className="relative font-mark text-[10px] uppercase tracking-[0.24em] text-[#1E3A6F] mb-4">
              {e.verb} — through the framework
            </div>
            <p className="relative text-sm sm:text-base text-slate-600 font-light leading-relaxed mb-6">
              {e.desc}
            </p>

            <ul className="relative space-y-2.5 mt-auto pt-5 border-t border-slate-100">
              {e.bullets.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-2.5 text-sm text-slate-700 font-light leading-snug"
                >
                  <CheckCircle
                    size={14}
                    weight="duotone"
                    className="text-[#1E3A6F] mt-1 flex-none"
                  />
                  {b}
                </li>
              ))}
            </ul>

            {e.cta && (
              <Link
                to={e.cta.to}
                data-testid={`${e.testId}-cta`}
                className="relative mt-6 inline-flex items-center gap-2 text-xs font-mark uppercase tracking-[0.22em] text-slate-900 hover:text-[#1E3A6F] transition-colors group/link"
              >
                <span className="block w-6 h-px bg-current transition-all duration-500 group-hover/link:w-10" />
                {e.cta.label}
                <ArrowRight size={12} weight="bold" className="transition-transform group-hover/link:translate-x-0.5" />
              </Link>
            )}
          </motion.div>
        ))}
      </div>

      {/* Featured Scoped Engagement — Payouts Modernization */}
      <div className="mt-20 pt-12 border-t border-slate-200">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
          variants={fadeUp}
          data-testid="featured-payouts-modernization"
          className="relative overflow-hidden bg-gradient-to-br from-[#0F1F3D] via-[#152A55] to-[#1E3A6F] text-white"
        >
          {/* Grain / rail accent */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          <div
            className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(120,160,220,0.35) 0%, transparent 70%)" }}
          />
          <div
            className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full blur-3xl opacity-30 pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(80,110,200,0.35) 0%, transparent 70%)" }}
          />

          <div className="relative grid lg:grid-cols-[1.15fr_1fr] gap-0">
            {/* Left: pitch */}
            <div className="p-8 sm:p-12 lg:p-16 border-b lg:border-b-0 lg:border-r border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-white text-[#1E3A6F] font-mark text-[9px] uppercase tracking-[0.22em] font-semibold">
                  <Sparkle size={9} weight="fill" /> Featured Scoped Engagement
                </span>
                <span className="font-mark text-[10px] uppercase tracking-[0.24em] text-white/60">
                  6–10 weeks · Fixed scope
                </span>
              </div>

              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.05] text-white">
                Payouts & Disbursements{" "}
                <span className="brand-emphasis text-white/90 italic">Modernization</span>
              </h3>

              <p className="mt-6 text-base sm:text-lg font-light text-white/80 leading-relaxed max-w-xl">
                Most disbursement programs are 5–10 years behind the rails available today. Wires by default. Reconciliation across three systems. No cost arbitrage between ACH, RTP, FedNow, push-to-debit, wire, or stablecoin. We fix that — end to end.
              </p>

              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="font-mark text-[10px] uppercase tracking-[0.24em] text-white/60 mb-4">
                  What ships
                </div>
                <ul className="space-y-3 max-w-lg">
                  {[
                    "Rail-by-rail cost model — ACH · RTP · FedNow · wire · push-to-debit · stablecoin",
                    "Routing logic based on amount, speed, geography, and cost thresholds",
                    "Bank & vendor consolidation with contract renegotiation",
                    "Reconciliation and exception ops unified into one workflow",
                    "Cross-border and on-chain optionality mapped for scale",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm sm:text-base text-white/90 font-light leading-snug">
                      <CheckCircle size={16} weight="duotone" className="text-white mt-0.5 flex-none" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href="#contact"
                data-testid="payouts-modernization-cta"
                className="mt-10 inline-flex items-center gap-2 text-xs font-mark uppercase tracking-[0.22em] text-white hover:text-white/80 transition-colors group/link"
              >
                <span className="block w-6 h-px bg-current transition-all duration-500 group-hover/link:w-10" />
                Scope a payouts review
                <ArrowRight size={12} weight="bold" className="transition-transform group-hover/link:translate-x-0.5" />
              </a>
            </div>

            {/* Right: rails / outcome grid */}
            <div className="p-8 sm:p-12 lg:p-14 flex flex-col justify-between gap-8">
              <div>
                <div className="font-mark text-[10px] uppercase tracking-[0.24em] text-white/60 mb-5">
                  Rails covered
                </div>
                <div className="grid grid-cols-2 gap-px bg-white/10">
                  {[
                    { icon: Bank, label: "ACH" },
                    { icon: Lightning, label: "RTP · FedNow" },
                    { icon: ArrowsLeftRight, label: "Wire" },
                    { icon: CreditCard, label: "Push to Debit" },
                    { icon: Wallet, label: "Digital Wallets" },
                    { icon: Coins, label: "Stablecoin" },
                  ].map((r) => {
                    const RIcon = r.icon;
                    return (
                      <div
                        key={r.label}
                        className="bg-[#152A55] hover:bg-[#1E3A6F] transition-colors duration-300 p-4 flex items-center gap-3"
                      >
                        <RIcon size={16} weight="duotone" className="text-white/80 flex-none" />
                        <span className="text-xs sm:text-sm text-white/90 font-light tracking-wide">
                          {r.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="pt-6 border-t border-white/10">
                <div className="font-mark text-[10px] uppercase tracking-[0.24em] text-white/60 mb-4">
                  Typical outcomes
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="font-display text-2xl sm:text-3xl font-light text-white tabular-nums">
                      Lower
                    </div>
                    <div className="text-xs text-white/60 font-light mt-1">
                      Blended cost per payout
                    </div>
                  </div>
                  <div>
                    <div className="font-display text-2xl sm:text-3xl font-light text-white tabular-nums">
                      Faster
                    </div>
                    <div className="text-xs text-white/60 font-light mt-1">
                      Time to funds — hours, not days
                    </div>
                  </div>
                  <div>
                    <div className="font-display text-2xl sm:text-3xl font-light text-white tabular-nums">
                      Fewer
                    </div>
                    <div className="text-xs text-white/60 font-light mt-1">
                      Vendors & bank accounts to run
                    </div>
                  </div>
                  <div>
                    <div className="font-display text-2xl sm:text-3xl font-light text-white tabular-nums">
                      Cleaner
                    </div>
                    <div className="text-xs text-white/60 font-light mt-1">
                      Recon, exceptions, and audit posture
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Domains covered row */}
      <div className="mt-20 pt-12 border-t border-slate-200">
        <div className="flex items-end justify-between flex-wrap gap-4 max-w-3xl">
          <div>
            <Overline>What's Covered</Overline>
            <h3 className="font-display mt-4 text-2xl sm:text-3xl font-light tracking-tight text-slate-900">
              Domain expertise across the{" "}
              <span className="font-mark font-semibold text-[#1E3A6F]">full lifecycle</span>.
            </h3>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-px bg-slate-200">
          {domains.map((d, i) => {
            const Icon = d.icon;
            return (
              <motion.div
                key={d.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                custom={i}
                variants={fadeUp}
                data-testid={`service-card-${d.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group bg-white hover:bg-slate-50 transition-all duration-300 p-6 flex flex-col"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-sm border border-slate-200 bg-slate-50 flex items-center justify-center group-hover:border-[#1E3A6F]/40 transition-colors">
                    <Icon size={18} weight="duotone" className="text-[#1E3A6F]" />
                  </div>
                  <span className="font-mark text-[9px] uppercase tracking-[0.22em] text-slate-500">
                    {d.side}
                  </span>
                </div>
                <h4 className="font-display text-lg font-medium text-slate-900 mb-2">
                  {d.title}
                </h4>
                <p className="text-sm text-slate-600 font-light leading-relaxed">
                  {d.summary}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </Section>
  );
};

// ---------- Approach ----------
const ApproachSection = () => {
  const steps = [
    {
      num: "01",
      title: "Assess",
      desc: "Audit the current state against all six pillars of the Flow Control Framework™.",
    },
    {
      num: "02",
      title: "Structure",
      desc: "Produce findings and a prioritized roadmap organized by pillar, impact, and effort.",
    },
    {
      num: "03",
      title: "Execute",
      desc: "Apply the roadmap — directly or alongside your team — pillar by pillar.",
    },
  ];
  return (
    <Section id="approach" testId="approach-section" className="border-t border-slate-200/60">
      <div className="max-w-3xl">
        <Overline>Approach</Overline>
        <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-[1.1]">
          I run the Framework{" "}
          <span className="brand-emphasis text-[#1E3A6F]">end to end</span>.
        </h2>
        <p className="mt-6 sm:mt-8 text-base sm:text-lg font-light text-slate-600 leading-relaxed">
          Three moves. Every engagement. Pillar by pillar — assess, structure, execute.
        </p>
      </div>

      <div className="mt-20 relative">
        {/* Animated flow rail connecting the 3 steps */}
        <div className="hidden lg:block absolute top-5 left-[8.333%] right-[8.333%] h-[2px] pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <svg
            className="absolute inset-0 w-full h-full overflow-visible"
            viewBox="0 0 1000 2"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="approachRail" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#C7D2DE" stopOpacity="0" />
                <stop offset="50%" stopColor="#C7D2DE" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#2C4A8C" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Traveling highlight segment */}
            <motion.rect
              y="0"
              height="2"
              width="180"
              fill="url(#approachRail)"
              initial={{ x: -200 }}
              animate={{ x: 1200 }}
              transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.6 }}
            />
          </svg>
        </div>

        <div className="grid lg:grid-cols-3 gap-12 lg:gap-16 relative">
          {steps.map((s, i) => (
            <motion.div
              key={s.num}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
              custom={i}
              variants={fadeUp}
              data-testid={`approach-step-${i + 1}`}
              className="relative"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="relative h-3.5 w-3.5 rounded-full bg-white z-10">
                  <div className="absolute inset-0 rounded-full bg-white animate-ping opacity-40" />
                  <div className="absolute -inset-2 rounded-full border border-slate-300" />
                </div>
                <span className="font-mark text-[11px] tracking-[0.28em] text-slate-400 uppercase">
                  Step {s.num}
                </span>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-medium text-slate-900 mb-4">
                {s.title}
              </h3>
              <p className="text-base text-slate-600 font-light leading-relaxed max-w-sm">
                {s.desc}
              </p>
              {i < steps.length - 1 && (
                <div className="lg:hidden mt-6 flex items-center gap-2">
                  <div className="h-px flex-1 bg-slate-200" />
                  <CaretRight size={14} weight="bold" className="text-slate-500" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="mt-20 flex items-center gap-4 pt-8 border-t border-slate-200/60"
      >
        <Lightning size={18} weight="duotone" className="text-slate-400" />
        <span className="text-sm text-slate-400 font-light italic">
          Interim Head of Payments available when the work calls for it.
        </span>
      </motion.div>
    </Section>
  );
};

// ---------- Differentiation ----------
// Framework Readiness Scorecard — sells the Flow Control Framework™ by showing
// how it diagnoses a real-world payments initiative across all six pillars.
const FRAMEWORK_SCENARIOS = [
  {
    id: "embedded",
    label: "Embedded Finance Stack",
    short: "Platform launching embedded accounts + payments",
    before: 28,
    after: 94,
    diagnosis: [
      { pillar: "Flow Design", status: "gap", note: "End-user fund flows vs. platform treasury undefined" },
      { pillar: "Control Layer", status: "gap", note: "No ownership between platform, BaaS partner, and sponsor bank" },
      { pillar: "Partner Architecture", status: "risk", note: "Sponsor-bank / BaaS / processor responsibilities overlap" },
      { pillar: "Economic Engine", status: "ok", note: "Interchange and rev-share modeled cleanly" },
      { pillar: "Execution Integrity", status: "gap", note: "AML / KYC / reg reporting split across vendors" },
      { pillar: "Scale Readiness", status: "risk", note: "Program caps and concentration thresholds unclear" },
    ],
  },
  {
    id: "card",
    label: "Card Program Launch",
    short: "Embedded card program",
    before: 34,
    after: 92,
    diagnosis: [
      { pillar: "Flow Design", status: "gap", note: "Funding flow undefined between issuer and treasury" },
      { pillar: "Control Layer", status: "gap", note: "No ownership between product, risk, and ops" },
      { pillar: "Partner Architecture", status: "risk", note: "BIN sponsor vs processor scope overlap" },
      { pillar: "Economic Engine", status: "ok", note: "Interchange model modeled, rev-share clean" },
      { pillar: "Execution Integrity", status: "risk", note: "No SLA monitoring on authorization" },
      { pillar: "Scale Readiness", status: "gap", note: "Program caps unclear past $50M spend" },
    ],
  },
  {
    id: "payout",
    label: "Payouts Modernization",
    short: "Domestic + international payouts",
    before: 41,
    after: 96,
    diagnosis: [
      { pillar: "Flow Design", status: "risk", note: "Wire-default despite RTP / FedNow eligibility" },
      { pillar: "Control Layer", status: "ok", note: "Treasury + AP alignment in place" },
      { pillar: "Partner Architecture", status: "gap", note: "Three banks, overlapping rails" },
      { pillar: "Economic Engine", status: "gap", note: "Rail-cost arbitrage not modeled" },
      { pillar: "Execution Integrity", status: "risk", note: "Reconciliation split across 3 systems" },
      { pillar: "Scale Readiness", status: "risk", note: "Stablecoin / cross-border optionality absent" },
    ],
  },
  {
    id: "stable",
    label: "Stablecoin Rail",
    short: "B2B stablecoin pilot",
    before: 22,
    after: 88,
    diagnosis: [
      { pillar: "Flow Design", status: "gap", note: "On-ramp / off-ramp path not specified" },
      { pillar: "Control Layer", status: "gap", note: "Custody decisions unresolved" },
      { pillar: "Partner Architecture", status: "risk", note: "Stablecoin issuer vs orchestration vendor" },
      { pillar: "Economic Engine", status: "ok", note: "FX + network fees modeled" },
      { pillar: "Execution Integrity", status: "gap", note: "AML / travel-rule posture undefined" },
      { pillar: "Scale Readiness", status: "risk", note: "Treasury sign-off blocked past pilot" },
    ],
  },
];

const StatusDot = ({ status }) => {
  const map = {
    ok: { cls: "bg-emerald-500", ring: "ring-emerald-500/30", label: "Clear" },
    risk: { cls: "bg-amber-500", ring: "ring-amber-500/30", label: "At risk" },
    gap: { cls: "bg-rose-500", ring: "ring-rose-500/30", label: "Gap" },
  };
  const m = map[status];
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`block w-2 h-2 rounded-full ${m.cls} ring-2 ${m.ring}`} />
      <span className="font-mark text-[10px] uppercase tracking-[0.18em] text-slate-500">
        {m.label}
      </span>
    </span>
  );
};

const ReadinessGauge = ({ before, after }) => {
  // simple arc gauge, 0-100 → angle 180°
  const size = 200;
  const cx = size / 2;
  const cy = 110;
  const r = 78;
  return (
    <div className="relative w-full">
      <svg viewBox={`0 0 ${size} 140`} className="w-full block">
        {/* background arc */}
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke="#E2E8F0"
          strokeWidth="12"
          strokeLinecap="round"
        />
        {/* before arc (muted) */}
        <motion.path
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: before / 100 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke="#94A3B8"
          strokeOpacity="0.5"
          strokeWidth="12"
          strokeLinecap="round"
        />
        {/* after arc (navy) */}
        <motion.path
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: after / 100 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke="#1E3A6F"
          strokeWidth="12"
          strokeLinecap="round"
        />
      </svg>
      {/* HTML label overlay for reliable typography */}
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-1 pointer-events-none">
        <div className="font-display text-3xl sm:text-4xl font-light text-slate-900 tabular-nums leading-none">
          {after}
          <span className="text-xl sm:text-2xl text-slate-500 ml-0.5">%</span>
        </div>
        <div className="mt-1 font-mark text-[9px] uppercase tracking-[0.28em] text-slate-500">
          Ready
        </div>
      </div>
    </div>
  );
};

const DifferentiationSection = () => {
  const [scenarioId, setScenarioId] = useState("embedded");
  const scenario =
    FRAMEWORK_SCENARIOS.find((s) => s.id === scenarioId) || FRAMEWORK_SCENARIOS[0];
  const bullets = [
    "10+ years in payments and fintech",
    "Experience across pay-ins, payouts, and card programs",
    "Background in large-scale deals and execution",
    "Direct, honest, and practical approach",
  ];
  return (
    <Section id="about" testId="differentiation-section" className="border-t border-slate-200/60">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* LEFT — Framework Readiness Scorecard */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <div className="relative w-full overflow-hidden rounded-sm border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 sm:p-7 lg:p-8">
            {/* Ambient navy glow */}
            <div
              className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-30 blur-3xl pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(30,58,111,0.5) 0%, transparent 65%)" }}
            />
            {/* Masked grid */}
            <div
              className="absolute inset-0 opacity-[0.04] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(30,58,111,1) 1px, transparent 1px), linear-gradient(90deg, rgba(30,58,111,1) 1px, transparent 1px)",
                backgroundSize: "28px 28px",
              }}
            />

            <div className="relative flex flex-col">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 font-mark text-[10px] uppercase tracking-[0.24em] text-slate-500">
                    <SpeedLines className="text-[#1E3A6F]" />
                    The Framework · Applied
                  </div>
                  <div className="mt-2 font-display text-lg sm:text-xl font-medium text-slate-900">
                    Framework Readiness Scorecard
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] font-mark uppercase tracking-[0.22em] text-emerald-700">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                  Live
                </div>
              </div>

              {/* Scenario tabs */}
              <div
                role="tablist"
                aria-label="Select scenario"
                className="mt-5 flex flex-wrap gap-1.5"
                data-testid="scenario-filter"
              >
                {FRAMEWORK_SCENARIOS.map((s) => {
                  const active = s.id === scenarioId;
                  return (
                    <button
                      key={s.id}
                      role="tab"
                      aria-selected={active}
                      data-testid={`scenario-${s.id}`}
                      onClick={() => setScenarioId(s.id)}
                      className={`px-2.5 py-1 rounded-sm font-mark text-[10px] uppercase tracking-[0.18em] transition-all border ${
                        active
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-900"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>

              {/* Gauge + headline */}
              <div className="mt-5 grid grid-cols-[auto_1fr] gap-4 sm:gap-6 items-center border-b border-slate-200 pb-5">
                <div className="w-[140px] sm:w-[180px]" key={`gauge-${scenario.id}`}>
                  <ReadinessGauge before={scenario.before} after={scenario.after} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mark">
                    Scenario
                  </div>
                  <div className="mt-1 font-display text-base sm:text-lg font-medium text-slate-900">
                    {scenario.short}
                  </div>
                  <div className="mt-3 flex items-baseline gap-2 sm:gap-3 flex-wrap">
                    <span className="font-display text-xl sm:text-2xl font-light text-slate-400 tabular-nums line-through decoration-slate-400/60">
                      {scenario.before}%
                    </span>
                    <ArrowRight size={14} weight="bold" className="text-slate-400" />
                    <motion.span
                      key={`after-${scenario.id}`}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="font-display text-2xl sm:text-3xl font-light text-[#1E3A6F] tabular-nums"
                    >
                      {scenario.after}%
                    </motion.span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mark">
                      readiness
                    </span>
                  </div>
                </div>
              </div>

              {/* Pillar-by-pillar diagnostic */}
              <div className="mt-5 space-y-2.5" key={`diag-${scenario.id}`}>
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mark">
                  <span>Diagnosed against 6 pillars</span>
                  <span className="hidden sm:inline">Output →</span>
                </div>
                {scenario.diagnosis.map((row, i) => (
                  <motion.div
                    key={`${scenario.id}-${row.pillar}`}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.45, delay: 0.06 + i * 0.05 }}
                    className="grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:gap-3 border-b border-slate-100 last:border-b-0 py-2"
                  >
                    <span className="font-display text-[11px] sm:text-xs text-[#1E3A6F] tabular-nums w-6">
                      0{i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="font-display text-xs sm:text-sm font-medium text-slate-900 truncate">
                        {row.pillar}
                      </div>
                      <div className="text-[11px] sm:text-xs text-slate-500 font-light leading-snug">
                        {row.note}
                      </div>
                    </div>
                    <StatusDot status={row.status} />
                  </motion.div>
                ))}
              </div>

              {/* Footer */}
              <div className="mt-5 pt-4 border-t border-slate-200 flex items-center justify-between flex-wrap gap-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mark">
                  Output: prioritized roadmap + named gaps
                </div>
                <div className="flex items-center gap-3 text-[10px] font-mark uppercase tracking-[0.2em] text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="block w-1.5 h-1.5 rounded-full bg-rose-500" /> Gap
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="block w-1.5 h-1.5 rounded-full bg-amber-500" /> Risk
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="block w-1.5 h-1.5 rounded-full bg-emerald-500" /> Clear
                  </span>
                </div>
              </div>
            </div>
          </div>

          <Link
            to="/sample"
            data-testid="view-sample-review"
            className="group mt-4 inline-flex items-center justify-between gap-3 w-full rounded-sm border border-slate-200 bg-white hover:bg-slate-900 hover:border-slate-900 transition-all duration-300 px-5 py-4"
          >
            <span className="flex items-center gap-3">
              <span className="flex-none w-9 h-9 rounded-sm border border-slate-200 bg-slate-50 group-hover:bg-white/10 group-hover:border-white/20 flex items-center justify-center transition-colors">
                <CaretRight size={14} weight="bold" className="text-slate-600 group-hover:text-white transition-colors" />
              </span>
              <span className="text-left">
                <span className="block font-mark text-[10px] uppercase tracking-[0.24em] text-slate-500 group-hover:text-slate-400 transition-colors">
                  See the deliverable
                </span>
                <span className="block mt-0.5 font-display text-sm sm:text-base text-slate-900 group-hover:text-white transition-colors">
                  View a sample Framework review
                </span>
              </span>
            </span>
            <ArrowRight
              size={16}
              weight="bold"
              className="text-slate-400 group-hover:text-white group-hover:translate-x-1 transition-all flex-none"
            />
          </Link>
        </motion.div>

        {/* RIGHT — Differentiation */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <Overline>Differentiation</Overline>
          <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-[1.1]">
            I built the framework{" "}
            <span className="brand-emphasis text-[#1E3A6F]">from inside payments.</span>
          </h2>
          <p className="mt-6 sm:mt-8 text-base sm:text-lg font-light text-slate-600 leading-relaxed max-w-xl">
            I've structured deals. I've negotiated with processors and banks.
            I've operated programs under real volume. The Flow Control
            Framework™ is how I do the work — not an after-the-fact
            rationalization.
          </p>

          <ul className="mt-10 sm:mt-12 space-y-px bg-slate-200">
            {bullets.map((b, i) => (
              <li
                key={b}
                data-testid={`diff-item-${i}`}
                className="flex items-center gap-4 sm:gap-6 bg-white border border-slate-200 shadow-sm hover:bg-slate-50 transition-colors px-5 sm:px-6 py-4 sm:py-5"
              >
                <span className="font-display text-xl sm:text-2xl font-light text-slate-500 tabular-nums">
                  0{i + 1}
                </span>
                <span className="text-sm sm:text-base text-slate-700 font-light">{b}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </Section>
  );
};

// ---------- Outcomes ----------
const OutcomesSection = () => {
  const items = [
    { icon: TrendUp, title: "Payment costs — named, modeled, defensible" },
    { icon: Handshake, title: "Partners — aligned to the flow, not inherited" },
    { icon: Gauge, title: "The system — structured, controlled, boring" },
    { icon: Stack, title: "Your team — running initiatives, not firefighting" },
    { icon: Lightning, title: "The stack — built to scale without rework" },
  ];
  return (
    <Section testId="outcomes-section" className="border-t border-slate-200/60 relative overflow-hidden">
      <div
        className="absolute inset-0 -z-10 opacity-[0.15]"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1704093240724-891c11815f42?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F6F7F5] via-[#F6F7F5]/95 to-[#F6F7F5]" />

      <div className="max-w-3xl">
        <Overline>Outcomes</Overline>
        <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-[1.1]">
          What the framework{" "}
          <span className="brand-emphasis text-[#1E3A6F]">produces.</span>
        </h2>
      </div>

      <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <motion.div
              key={it.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              custom={i}
              variants={fadeUp}
              data-testid={`outcome-${i}`}
              className="group bg-white border border-slate-200 shadow-sm border border-slate-200/60 hover:border-slate-300 transition-all duration-300 p-8 rounded-sm hover:-translate-y-1"
            >
              <Icon size={28} weight="duotone" className="text-slate-600 mb-6" />
              <h3 className="font-display text-lg font-medium text-slate-900 leading-snug">
                {it.title}
              </h3>
              <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                <span className="block w-6 h-px bg-slate-500 group-hover:w-12 group-hover:bg-white transition-all" />
                <CheckCircle size={14} weight="duotone" />
              </div>
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
};

// ---------- Why Companies Work With Me ----------
const WhyMeSection = () => {
  const pillars = [
    {
      icon: CurrencyDollar,
      title: "Where costs hide",
      desc: "Fee structures, interchange, routing, and contract terms — the places margin quietly disappears across acquiring and issuing.",
    },
    {
      icon: Handshake,
      title: "Where deals break",
      desc: "Processor, acquirer, and issuer negotiations, partnership structures, and incentives — how to structure real leverage.",
    },
    {
      icon: Shield,
      title: "Where compliance adds scope",
      desc: "PCI DSS, network rules, state-level surcharge law, and BSA / AML — where scope creeps and how to reduce it responsibly.",
    },
    {
      icon: Gauge,
      title: "Where systems fail under pressure",
      desc: "Operational limits, fragility, reconciliation, and scale — what breaks before it should.",
    },
  ];
  return (
    <Section testId="why-me-section" className="border-t border-slate-200/60">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <Overline>Why Me</Overline>
          <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-[1.1]">
            I fix payments{" "}
            <span className="brand-emphasis text-[#1E3A6F]">from the inside.</span>
          </h2>
          <p className="mt-8 text-base sm:text-lg font-light text-slate-700 leading-relaxed">
            I've built, sold, and scaled payment systems at operator scale. The
            framework is where that experience lives.
          </p>
          <p className="mt-4 text-sm sm:text-base font-light text-slate-500 leading-relaxed">
            I know where costs hide, where deals break, where compliance adds
            scope, and where systems fail under pressure. I find the problem
            quickly — and I fix it without adding complexity.
          </p>
        </div>
        <div className="lg:col-span-7">
          <ul className="flex flex-col gap-px bg-slate-200">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.li
                  key={p.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  data-testid={`why-me-${i}`}
                  className="group bg-white hover:bg-slate-50 transition-colors p-8 lg:p-10"
                >
                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 flex-none rounded-sm border border-slate-200 flex items-center justify-center bg-slate-100 group-hover:border-blue-300/40 transition-colors">
                      <Icon size={22} weight="duotone" className="text-[#1E3A6F]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-display text-xl sm:text-2xl font-medium text-slate-900">
                          {p.title}
                        </h3>
                        <span className="font-mark text-[11px] tracking-[0.24em] text-slate-500">
                          0{i + 1}
                        </span>
                      </div>
                      <p className="mt-3 text-base text-slate-600 font-light leading-relaxed max-w-xl">
                        {p.desc}
                      </p>
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </ul>
        </div>
      </div>
    </Section>
  );
};

// ---------- Who This Is For ----------
const WhoForSection = () => {
  const triggers = [
    {
      num: "01",
      title: "Board asked why your take rate is degrading.",
      note: "Cost structure. Interchange. Processor economics.",
    },
    {
      num: "02",
      title: "Your card program is stuck at the sponsor bank.",
      note: "Program governance. BIN sponsor scope. Time-to-launch.",
    },
    {
      num: "03",
      title: "Your Head of Payments left — or you never hired one.",
      note: "Fractional coverage. Continuity through the gap.",
    },
    {
      num: "04",
      title: "You're launching embedded finance and BaaS complexity is mounting.",
      note: "Sponsor-bank selection. Stack decisions. Compliance ownership.",
    },
    {
      num: "05",
      title: "Auth rates are drifting and no one owns the fix.",
      note: "Diagnostic across pillars 03–05. Named ownership.",
    },
    {
      num: "06",
      title: "You're evaluating processors, orchestrators, or BaaS partners.",
      note: "Structured RFP. Contract negotiation. Vendor architecture.",
    },
  ];
  return (
    <Section testId="who-for-section" className="border-t border-slate-200/60">
      <div className="max-w-4xl">
        <Overline>Who This Is For</Overline>
        <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
          Fintech and platforms{" "}
          <span className="brand-emphasis text-[#1E3A6F]">scaling past $100M in payment volume</span>.
        </h2>
        <p className="mt-6 sm:mt-8 text-base sm:text-lg font-light text-slate-600 leading-relaxed max-w-3xl">
          Series B to pre-IPO. CFOs, founders, and Heads of Payments. Companies
          where payments has stopped being a side-of-desk function and started
          determining unit economics, ship velocity, and enterprise value.
        </p>
      </div>

      {/* Trigger Events */}
      <div className="mt-14 sm:mt-16 max-w-4xl">
        <div className="font-mark text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-6 flex items-center gap-3">
          <span className="block w-6 h-px bg-slate-300" />
          You should call when
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {triggers.map((t, i) => (
            <motion.div
              key={t.num}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              data-testid={`trigger-item-${i}`}
              className="bg-white p-5 sm:p-6 flex items-start gap-4"
            >
              <span className="font-display text-xs text-[#1E3A6F] tabular-nums tracking-[0.18em] pt-1 flex-none">
                {t.num}
              </span>
              <div className="min-w-0">
                <div className="font-display text-sm sm:text-base font-medium text-slate-900 leading-snug">
                  {t.title}
                </div>
                <div className="mt-1.5 text-[11px] sm:text-xs text-slate-500 font-light leading-snug">
                  {t.note}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Anti-ICP disqualifier */}
      <div className="mt-14 sm:mt-16 max-w-3xl border-l-2 border-slate-900 pl-6 py-2">
        <div className="font-mark text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">
          Not the right fit
        </div>
        <p className="font-display text-base sm:text-lg text-slate-800 leading-relaxed">
          Pre-Series-A startups. SMBs looking for a discount audit. Companies
          shopping for a deck. Enterprises that need a Big-Four bench.
          <br />
          <span className="text-slate-900 font-medium">
            This is principal-led operator work. If that isn't what you're
            looking for, we're not the right firm.
          </span>
        </p>
      </div>
    </Section>
  );
};

// ---------- FAQ Section ----------
const FAQ_ITEMS = [
  {
    q: "What is The Flow Control Framework™?",
    a: "A six-pillar system built on the two foundations of any payment system: how money moves (Flow) and who owns each step (Control). Four more diagnostic lenses extend from there — Partner Architecture, Economic Engine, Execution Integrity, and Scale Readiness. The same lens, every initiative: a BNPL pilot, a stablecoin payout rail, a card program, agentic commerce. One method, one language.",
  },
  {
    q: "Who is the right fit for an engagement?",
    a: "Fintech and platforms scaling past $100M in payment volume — Series B through pre-IPO. CFOs, founders, and Heads of Payments where payments has stopped being a side-of-desk function and started determining unit economics, ship velocity, and enterprise value. Principal-led operator work. Not a fit for pre-Series-A startups, SMB audits, or teams shopping for a deck.",
  },
  {
    q: "How long does a typical engagement take?",
    a: "A Payments Review runs 2–4 weeks end-to-end. Fractional Head of Payments engagements are monthly with a defined scope. Project-based framework applications (e.g., a card-program launch or payouts modernization) are scoped after the initial call.",
  },
  {
    q: "What does a Payments Review actually produce?",
    a: "A structured, pillar-by-pillar diagnosis of the current state, named gaps and risks across the six pillars, prioritized recommendations with impact estimates, and a 30-60-90 day roadmap. The /sample deliverable shows the format.",
  },
  {
    q: "Is the first call paid?",
    a: "No. The first 20–30 minute call is complimentary and used to assess fit, scope the problem, and determine whether a formal engagement makes sense. No obligation.",
  },
  {
    q: "Do you replace our existing payments team?",
    a: "No. The framework is designed to work alongside internal teams — clarifying ownership, accountability, and decisions without displacing operators. Fractional engagements specifically fill the Head-of-Payments gap between hires.",
  },
  {
    q: "Which rails and modalities do you cover?",
    a: "Pay-ins (cards CP / CNP, ACH, wallets), payouts & disbursements (ACH, RTP, FedNow, wire, push-to-card, stablecoin), issuing / card programs, stored value and gift, and emerging rails including agentic-commerce flows.",
  },
  {
    q: "How do engagements typically start?",
    a: "Get a Flow Control Review via the form. A short call follows within 2–3 business days. If there is a fit, a scoped proposal lands within a week.",
  },
];

const FAQItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay: index * 0.04 }}
      className="border-b border-slate-200 last:border-b-0"
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-start justify-between gap-6 text-left py-5 sm:py-6 group"
      >
        <span className="flex items-start gap-4 sm:gap-5 min-w-0">
          <span className="font-display text-sm text-[#1E3A6F] tabular-nums flex-none pt-0.5">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="font-display text-base sm:text-lg text-slate-900 font-medium leading-snug">
            {q}
          </span>
        </span>
        <span
          className={`flex-none mt-1 w-7 h-7 rounded-full border border-slate-300 flex items-center justify-center transition-all duration-300 group-hover:border-[#1E3A6F] ${
            open ? "bg-slate-900 border-slate-900 rotate-45" : "bg-white"
          }`}
          aria-hidden="true"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M5 1V9M1 5H9"
              stroke={open ? "#FFFFFF" : "#1E3A6F"}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p className="pb-6 pl-10 sm:pl-12 pr-10 text-sm sm:text-base text-slate-600 font-light leading-relaxed">
            {a}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// ---------- What You Receive ----------
const WhatYouReceiveSection = () => {
  const artifacts = [
    {
      num: "01",
      title: "Pillar-by-pillar diagnosis",
      desc: "Your full payment system mapped through Flow Design, Control Layer, Partner Architecture, Economic Engine, Execution Integrity, and Scale Readiness — with a Gap / Risk / Clear status on every pillar.",
    },
    {
      num: "02",
      title: "Named gaps, ranked by risk",
      desc: "Every structural gap surfaced with a plain-English explanation of why it matters, the dollar or operational exposure, and the pillar it lives under. No 'further investigation needed.'",
    },
    {
      num: "03",
      title: "Prioritized roadmap with quantified impact",
      desc: "Each recommendation tied to a pillar, scored on impact and effort, and modeled with a defensible economic estimate. You can take this directly to the board.",
    },
    {
      num: "04",
      title: "30 · 60 · 90 day execution plan",
      desc: "A sequenced playbook — quick wins, structural work, strategic moves — with vendor, partner, and internal-team accountability called out by name.",
    },
  ];
  return (
    <Section testId="what-you-receive-section" className="border-t border-slate-200/60">
      <div className="max-w-5xl">
        <Overline>What You Receive</Overline>
        <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
          The deliverable.{" "}
          <span className="brand-emphasis text-[#1E3A6F]">Not a deck. A diagnosis.</span>
        </h2>
        <p className="mt-6 sm:mt-8 text-base sm:text-lg font-light text-slate-600 leading-relaxed max-w-3xl">
          Every Flow Control Review produces the same four artifacts — applied to your
          volume, mix, and architecture. Concrete. Boardroom-ready. Built to be acted on.
        </p>
      </div>

      <div className="mt-10 sm:mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
        {artifacts.map((a, i) => (
          <motion.div
            key={a.num}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: i * 0.07 }}
            data-testid={`receive-item-${i}`}
            className="bg-white p-6 sm:p-8 flex flex-col"
          >
            <div className="flex items-baseline gap-3">
              <span className="font-display text-xs text-[#1E3A6F] tabular-nums tracking-[0.18em]">
                {a.num}
              </span>
              <span className="font-mark text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Artifact
              </span>
            </div>
            <h3 className="mt-4 font-display text-xl sm:text-2xl font-medium text-slate-900 leading-snug">
              {a.title}
            </h3>
            <p className="mt-3 text-sm sm:text-base text-slate-600 font-light leading-relaxed">
              {a.desc}
            </p>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 sm:mt-14 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <Link
          to="/sample"
          data-testid="receive-view-sample"
          className="group inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-[#1E3A6F] transition-all duration-200 px-7 py-3.5 rounded-sm text-sm font-medium hover:scale-[1.02]"
        >
          See a sample deliverable
          <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
        <span className="text-xs uppercase tracking-[0.24em] text-slate-500 font-mark">
          PDF · Confidential · 18–24 pages
        </span>
      </div>
    </Section>
  );
};

const FAQSection = () => (
  <Section id="faq" testId="faq-section" className="border-t border-slate-200/60">
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-4">
        <Overline>FAQ</Overline>
        <h2 className="font-display mt-4 sm:mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
          Questions, answered{" "}
          <span className="brand-emphasis text-[#1E3A6F]">directly.</span>
        </h2>
        <p className="mt-6 text-sm sm:text-base text-slate-600 font-light leading-relaxed">
          Eight of the most common questions founders and operators ask before
          reaching out. If yours isn't covered, just ask.
        </p>
        <button
          onClick={() => {
            const btn = document.querySelector('[data-testid="nav-cta-button"]');
            if (btn) btn.click();
          }}
          data-testid="faq-cta"
          className="mt-6 sm:mt-8 inline-flex items-center gap-2 text-sm font-medium text-slate-900 border-b border-slate-900 hover:gap-3 transition-all"
        >
          Ask a different question
          <ArrowRight size={14} weight="bold" />
        </button>
      </div>
      <div className="lg:col-span-8">
        <div className="border-t border-slate-200">
          {FAQ_ITEMS.map((item, i) => (
            <FAQItem key={item.q} {...item} index={i} />
          ))}
        </div>
      </div>
    </div>
  </Section>
);

// ---------- Sticky Mobile CTA (conversion lever) ----------
const StickyMobileCTA = ({ onOpenForm }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handler = () => {
      // Show after 500px scroll, hide at the very top and near the final CTA
      const scrolled = window.scrollY;
      const atFinalCTA = (() => {
        const el = document.getElementById("contact");
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return rect.top < window.innerHeight * 1.2;
      })();
      setVisible(scrolled > 500 && !atFinalCTA);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return (
    <div
      data-testid="sticky-mobile-cta"
      className={`md:hidden fixed left-0 right-0 bottom-0 z-40 px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-3 transition-all duration-300 pointer-events-none ${
        visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      }`}
      style={{
        background:
          "linear-gradient(to top, rgba(246,247,245,0.98) 55%, rgba(246,247,245,0))",
      }}
    >
      <button
        onClick={onOpenForm}
        data-testid="sticky-mobile-cta-btn"
        className="pointer-events-auto w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-6 py-3.5 rounded-sm text-sm font-medium shadow-[0_8px_24px_rgba(15,23,42,0.18)]"
      >
        Get a Flow Control Review
        <ArrowRight size={16} weight="bold" />
      </button>
      <div className="mt-1.5 text-center text-[10px] font-mark uppercase tracking-[0.22em] text-slate-500">
        Free 20-min call · No obligation
      </div>
    </div>
  );
};

// ---------- Final CTA ----------
const FinalCTA = ({ onOpenForm }) => (
  <Section id="contact" testId="final-cta-section" className="border-t border-slate-200/60">
    <div className="relative overflow-hidden rounded-sm border border-slate-900 bg-gradient-to-br from-slate-900 to-[#0A0D14] p-10 sm:p-16 lg:p-24">
      <div
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle, #1E3A6F 0%, transparent 60%)" }}
      />
      <div className="relative max-w-3xl">
        <span className="inline-flex items-center gap-2.5 font-mark text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-300">
          <SpeedLines className="text-[#93a9c9]" />
          Get Started
        </span>
        <h2 className="font-display mt-6 text-3xl sm:text-4xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
          Turn payments infrastructure into your{" "}
          <span className="font-mark font-semibold text-[#C7D2DE]">operating leverage</span>.
        </h2>
        <p className="mt-6 sm:mt-8 text-base sm:text-lg font-light text-slate-300 leading-relaxed max-w-2xl">
          A payments function that compounds — lower costs, faster launches,
          defensible economics, and a system built to scale without breaking.
          The Flow Control Framework™ is how we get there.
        </p>
        <div className="mt-12 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <button
            onClick={onOpenForm}
            data-testid="final-cta-primary"
            className="group inline-flex items-center justify-center gap-2 bg-white text-slate-900 hover:bg-slate-100 transition-all duration-200 px-8 py-4 rounded-sm text-base font-medium hover:scale-[1.02]"
          >
            Get a Flow Control Review
            <ArrowRight
              size={18}
              weight="bold"
              className="group-hover:translate-x-1 transition-transform"
            />
          </button>
          <span className="text-xs uppercase tracking-[0.24em] text-slate-400">
            Principal-led · Limited engagements
          </span>
        </div>
        <div className="mt-6 flex items-center gap-2 text-xs text-slate-400 font-light">
          <CheckCircle size={14} weight="fill" className="text-emerald-400 flex-none" />
          <span>
            Free 20-min call · No obligation · Scoped proposal within a week
          </span>
        </div>
      </div>
    </div>
  </Section>
);

// ---------- Footer ----------
const Footer = () => (
  <footer
    data-testid="footer-section"
    className="border-t border-slate-200 bg-[#F6F7F5]"
  >
    <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
      <div className="grid md:grid-cols-3 gap-12 md:gap-8">
        <div className="md:col-span-1">
          <div className="flex items-center gap-4">
            <img
              src={LOGO_URL}
              alt="Cody Payments Consulting"
              className="h-24 w-24 sm:h-28 sm:w-28 object-contain"
            />
          </div>
          <p className="mt-6 text-sm text-slate-400 font-light max-w-xs leading-relaxed">
            Payments systems structured through the Flow Control Framework™.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 font-mark text-[10px] uppercase tracking-[0.32em] text-slate-500">
            <SpeedLines className="text-[#2C4A8C]" /> Built on real experience
          </div>
        </div>

        <div>
          <Overline>Navigate</Overline>
          <ul className="mt-5 space-y-3">
            {[
              { label: "Framework", href: "#framework" },
              { label: "Engagement", href: "#services" },
              { label: "About", href: "#about" },
              { label: "Contact", href: "#contact" },
            ].map((l) => (
              <li key={l.label}>
                <a
                  href={l.href}
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                  className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <Overline>Connect</Overline>
          <ul className="mt-5 space-y-3">
            <li>
              <a
                href="https://www.linkedin.com"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-linkedin"
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <LinkedinLogo size={16} weight="duotone" />
                LinkedIn
              </a>
            </li>
            <li>
              <a
                href="#contact"
                data-testid="footer-cta"
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowRight size={14} weight="bold" />
                Get a Flow Control Review
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <span className="text-xs text-slate-500 tracking-wide">
          © {new Date().getFullYear()} Cody Payments Consulting. All rights reserved.
        </span>
        <span className="text-xs text-slate-500 tracking-[0.2em] uppercase">
          Built on real experience
        </span>
      </div>
    </div>
  </footer>
);

// ---------- Consultation Form Dialog ----------
const ConsultationDialog = ({ open, onOpenChange }) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    payment_volume: "",
    interest_area: "",
    message: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.message) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, {
        ...form,
        role: form.role || null,
        payment_volume: form.payment_volume || null,
        interest_area: form.interest_area || null,
      });
      toast.success("Request received. I'll be in touch shortly.");
      onOpenChange(false);
      setForm({
        name: "",
        email: "",
        company: "",
        role: "",
        payment_volume: "",
        interest_area: "",
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not submit. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="consultation-dialog"
        className="bg-white border border-slate-200 text-slate-900 max-w-xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <span className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
            Get a Flow Control Review
          </span>
          <DialogTitle className="font-display text-2xl sm:text-3xl font-light text-slate-900 pt-2">
            Tell me about your payments stack.
          </DialogTitle>
          <DialogDescription className="text-slate-400 font-light">
            A short note sharpens the review. I respond personally to every
            qualified request.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-600">Name *</Label>
              <Input
                id="name"
                data-testid="form-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="bg-white border-slate-300 text-slate-900 focus:border-slate-500"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-600">Email *</Label>
              <Input
                id="email"
                data-testid="form-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="bg-white border-slate-300 text-slate-900 focus:border-slate-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company" className="text-slate-600">Company *</Label>
              <Input
                id="company"
                data-testid="form-company"
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                className="bg-white border-slate-300 text-slate-900 focus:border-slate-500"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role" className="text-slate-600">Role</Label>
              <Input
                id="role"
                data-testid="form-role"
                value={form.role}
                onChange={(e) => update("role", e.target.value)}
                className="bg-white border-slate-300 text-slate-900 focus:border-slate-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-600">Annual Payment Volume</Label>
              <Select
                value={form.payment_volume}
                onValueChange={(v) => update("payment_volume", v)}
              >
                <SelectTrigger
                  data-testid="form-volume"
                  className="bg-white border-slate-300 text-slate-900"
                >
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-slate-200 shadow-sm border-slate-200 text-slate-900">
                  <SelectItem value="<$10M">Less than $10M</SelectItem>
                  <SelectItem value="$10M-$100M">$10M – $100M</SelectItem>
                  <SelectItem value="$100M-$1B">$100M – $1B</SelectItem>
                  <SelectItem value=">$1B">$1B+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600">Primary Interest</Label>
              <Select
                value={form.interest_area}
                onValueChange={(v) => update("interest_area", v)}
              >
                <SelectTrigger
                  data-testid="form-interest"
                  className="bg-white border-slate-300 text-slate-900"
                >
                  <SelectValue placeholder="Select area" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-slate-200 shadow-sm border-slate-200 text-slate-900">
                  <SelectItem value="Pay-ins">Pay-ins</SelectItem>
                  <SelectItem value="Payouts">Payouts & Disbursements</SelectItem>
                  <SelectItem value="Card Programs">Card Programs</SelectItem>
                  <SelectItem value="Gift Cards">Gift Cards</SelectItem>
                  <SelectItem value="General Strategy">General Strategy</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-slate-600">What are you working on? *</Label>
            <Textarea
              id="message"
              data-testid="form-message"
              rows={5}
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              className="bg-white border-slate-300 text-slate-900 focus:border-slate-500 resize-none"
              required
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <span className="text-xs text-slate-500 tracking-wide">
              No spam. Direct response.
            </span>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="form-submit"
              className="bg-slate-900 text-white hover:bg-[#1E3A6F] px-6 py-2.5 rounded-sm"
            >
              {submitting ? "Sending..." : "Send Request"}
              <ArrowRight size={16} weight="bold" className="ml-2" />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Page ----------
export default function LandingPage() {
  const [formOpen, setFormOpen] = useState(false);
  const open = () => setFormOpen(true);

  useEffect(() => {
    document.title =
      "Cody Payments Consulting — Payments Infrastructure That Compounds";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "We turn payments infrastructure into operating leverage. The Flow Control Framework™ applied through fractional leadership, diagnostic reviews, and scoped execution. For fintech, platforms, and enterprises."
      );
    }
  }, []);

  return (
    <div className="bg-[#F6F7F5] text-slate-900 min-h-screen font-body relative overflow-x-hidden" data-testid="landing-page">
      <PaymentFlowBackground />
      <Toaster
        theme="light"
        position="top-right"
        toastOptions={{
          style: {
            background: "#FFFFFF",
            border: "1px solid rgba(15,23,42,0.12)",
            color: "#0A0D14",
          },
        }}
      />
      <Navbar onOpenForm={open} />
      <main>
        <Hero onOpenForm={open} />
        <MarqueeStrip />
        <ProblemSection />
        <SelectedExperienceSection />
        <FlowControlFramework />
        <MethodologySection />
        <ServicesSection />
        <ApproachSection />
        <DifferentiationSection />
        <WhyMeSection />
        <StatsSection />
        <OutcomesSection />
        <WhoForSection />
        <WhatYouReceiveSection />
        <FAQSection />
        <FinalCTA onOpenForm={open} />
      </main>
      <Footer />
      <StickyMobileCTA onOpenForm={open} />
      <ConsultationDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
